import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal, Dropdown, DropdownButton } from 'react-bootstrap'
import Select, { components } from 'react-select'
import MaskedInput from 'react-text-mask'

import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import useTableDragScroll from 'pages/parts/useTableDragScroll'
import DeleteModal from 'pages/parts/DeleteModal'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import TableDropdown from 'helpers/TableDropdown'
import ProductsAiModal from './ProductsAiModal'
import PriceHistory from './PriceHistory'

import { httpOk, GET, DELETE, FILE_D, PUT, FILE } from 'helpers/api'
import {
	buildFilterParams,
	parseProductTableOrderBy,
	buildProductTableOrderBy,
} from 'helpers/productFilterParams'
import {
	PRODUCTS_TREE_VIEW_PAGE_SIZE,
	buildMenuProductCounts,
	buildMenuProductTreeRows,
} from 'helpers/productTreeView'
import { renderSortIndicator, renderSortDirectionIcon } from 'helpers/tableSortIndicator'
import { findKeyFromArrayByValue, formatMoney, returnSign, PAGE_SIZES, notify } from "helpers/helpers"
import { subscribeProductsChanged } from './productsSync'
import CustomTooltip from "pages/parts/CustomTooltip"

function Index() {
	const { t } = useTranslation()
	const location = useLocation()

	const reduxAccount = useSelector(state => state.account)

	const statuses = [
		{ "id": 'active', "name": t('all_active') },
		{ "id": 1, "name": t('on_sale') },
		{ "id": 0, "name": t('off_sale') },
		{ "id": -1, "name": t('deleted') },
	]

	const columnFilers = [
		{ "id": 'fiscal', "name": t('fiscalization') },
		{ "id": 'cooking_time', "name": t('cooking_time') },
		{ "id": 'menu', "name": t('menu') },
		{ "id": 'preparation_place', "name": t('preparation_places') },
		{ "id": 'image', "name": t('image2') },
		{ "id": 'telegram_bot', "name": t('external_service') },
		{ "id": 'ingredients', "name": t('ingredients') },
		{ "id": 'is_service_charge_enabled', "name": t('service') },
	]

	const writeOffTypes = [
		{ id: 1, name: t('ingredients') },
		{ id: 2, name: t('product') },
	]

	const [data, setData] = useState([])
	const [menu, setMenu] = useState([])
	const [poses, setPoses] = useState([])
	const [categories, setCategories] = useState([])
	const [wms, setWms] = useState([])
	const [productTypes, setProductTypes] = useState([])
	const [preparationPlaces, setPreparationPlaces] = useState([])
	const [selectedItem, setSelectedItem] = useState({})
	const [modalCostPrice, setModalCostPrice] = useState(false)
	const [modalPriceHistory, setModalPriceHistory] = useState(false)

	const [fiscalCodes, setFiscalCodes] = useState([])
	const [taxCategories, setTaxCategories] = useState([])
	const [fiscalPackageCodes, setFiscalPackageCodes] = useState([])

	const [modalFiscal, setModalFiscal] = useState(false)
	const [modalCookingTime, setModalCookingTime] = useState(false)
	const [modalTaxCategory, setModalTaxCategory] = useState(false)
	const [modalCategory, setModalCategory] = useState(false)
	const [modalPreparationPlace, setModalPreparationPlace] = useState(false)
	const [modalWms, setModalWms] = useState(false)
	const [modalWriteOffType, setModalWriteOffType] = useState(false)
	const [modalMultipleDelete, setModalMultipleDelete] = useState(false)
	const [modalOption, setModalOption] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [modalAi, setModalAi] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0, index: 0 })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [viewMode, setViewMode] = useState('table')
	const [expandedMenuIds, setExpandedMenuIds] = useState(() => new Set())
	const tableDragScroll = useTableDragScroll()
	const {
		filterData,
		appliedFilterData,
		setFilterData,
		setAppliedFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		patchFilterData,
		applySearch,
		applyFilters,
		paginate,
	} = useFilterSearchPagination({
		initialFilterData: {
			'pos_id': reduxAccount.pos_id,
			'wms_id': null,
			'status': 1,
			'size': 20,
			'search': '',
			'category_id': null,
			'menu_ids': [],
		},
		initialPagination: {
			url: '/products',
			size: 20,
			response: null,
		},
	})

	function productSortStateForIndicator(fd) {
		const { field, sort } = parseProductTableOrderBy(fd.order_by)
		return { order_by: field, sort }
	}

	function productSortFieldLabel(field) {
		if (field === "name") return t("name2")
		if (field === "sale_price") return t("sale_price")
		if (field === "profit") return t("income")
		if (field === "cost_price_percent") return `${t("cost_price")}%`
		if (field === "markup_percent") return `${t("markup")}%`
		if (field === "profit_percent") return `${t("margin")}%`
		return field
	}

	function handleProductTableSort(fieldKey) {
		const { field: currentField, sort: currentSort } = parseProductTableOrderBy(filterData.order_by)
		const nextSort = currentField === fieldKey ? (currentSort === "asc" ? "desc" : "asc") : "asc"
		const order_by = buildProductTableOrderBy(fieldKey, nextSort)
		const nextFilterData = { ...filterData, order_by, page: null }
		patchFilterData({ order_by, page: null })
		searchWithFilter({ filterData: nextFilterData })
	}

	async function searchWithFilter(passedData = {}) {
		var filterDataCopy = { ...filterData, ...(passedData.filterData || {}) }
		const activeViewMode = passedData.viewMode ?? viewMode

		if (activeViewMode === 'tree') {
			filterDataCopy = {
				...filterDataCopy,
				size: PRODUCTS_TREE_VIEW_PAGE_SIZE,
				page: null,
			}
		}

		var filterUrl = "/products"
		var urlParams = buildFilterParams(filterDataCopy)
		checkFilter(filterDataCopy)

		if (`/products${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams
		var response = await GET(filterUrl, {}, { loader: !passedData.silent })
		if (httpOk(response)) {
			setData(response.data.data)
			setAppliedFilterData(filterDataCopy)
			setPagination({
				...pagination,
				'url': filterUrl,
				'size': filterDataCopy.size,
				'response': response.data,
			})
		}

		setDropdownIsOpen(false)
	}

	async function updateCostPrice(e) {
		e.preventDefault();

		var response = await PUT('/products-change-price', {
			'pos_id': reduxAccount.pos_id,
			'product_id': selectedItem.id,
			'cost_price': selectedItem.cost_price,
			'sale_price': selectedItem.sale_price,
		}, { loader: true })

		if (httpOk(response)) {
			searchWithFilter()
			toggleModalCostPrice()
		}
	}

	async function deleteMultipleProducts() {
		var response = await PUT('/products-multiple-delete', {
			'menus': selectedItem.menus,
			'product_types': selectedItem.product_types,
			'delete_all': Boolean(selectedItem.delete_all),
		}, { loader: true })

		if (httpOk(response)) {
			searchWithFilter()
			toggleModalMultipleDelete()
		}
	}

	function handleInputChange(e, index) {
		var dataCopy = [...data]
		dataCopy[index][e.target.name] = e.target.value
		setData(dataCopy)
	}

	async function updateSalePrice(index) {
		var dataCopy = [...data]

		const response = await PUT('/products-price', {
			'pos_id': filterData.pos_id,
			'product_id': dataCopy[index]['id'],
			'sale_price': dataCopy[index]['sale_price'],
		}, { loader: true })

		if (httpOk(response)) searchWithFilter()
	}

	async function downloadExcel() {
		// Как в таблице: применённые фильтры, не черновик из дропдауна
		var filterDataCopy = { ...appliedFilterData, page: null }
		var filterUrl = "/products"
		var urlParams = buildFilterParams(filterDataCopy)
		urlParams += returnSign(urlParams) + 'excel=true'

		const response = await FILE_D(filterUrl + urlParams)
		if (httpOk(response)) {
			// Создаем объект Blob из полученного файла
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

			// Создаем ссылку для скачивания файла
			const fileUrl = URL.createObjectURL(file);

			// Создаем ссылку для скачивания файла в браузере
			const link = document.createElement('a');
			link.href = fileUrl;
			link.setAttribute('download', `products.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Удаляем ссылку после скачивания файла
			URL.revokeObjectURL(fileUrl);
		}
	}

	async function downloadExcelRecipeFile() {
		var filterDataCopy = { ...appliedFilterData, page: null }
		var filterUrl = "/products-ingredients-excel"
		var urlParams = buildFilterParams(filterDataCopy)

		var response = await FILE_D(filterUrl + urlParams)
		if (httpOk(response)) {
			// Создаем объект Blob из полученного файла
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

			// Создаем ссылку для скачивания файла
			const fileUrl = URL.createObjectURL(file);

			// Создаем ссылку для скачивания файла в браузере
			const link = document.createElement('a');
			link.href = fileUrl;
			link.setAttribute('download', `${t('recipes')}.xlsx`);
			document.body.appendChild(link);
			link.click();

			// Удаляем ссылку после скачивания файла
			URL.revokeObjectURL(fileUrl);
		}
	}

	function resetFilter() {
		const allMenus = flattenTree(menu || [], null)
		const nextFilterData = {
			pos_id: reduxAccount.pos_id,
			wms_id: null,
			status: 1,
			size: 20,
			search: '',
			category_id: null,
			menu_ids: allMenus,
		}
		setFilterData(nextFilterData)
		setAppliedFilterData(nextFilterData)
		setFilterDataExist(false)
		searchWithFilter({ filterData: nextFilterData })
	}

	function checkFilter(currentFilterData) {
		if (
			currentFilterData.pos_id === null &&
			currentFilterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function toggleModalCostPrice(bool, item = {}) {
		// cost_price приходит numeric-строкой ("100.00") — number-инпут показал бы её как "100,00"
		setSelectedItem({
			...item,
			cost_price: (item.cost_price ?? '') === '' ? '' : Number(item.cost_price),
			sale_price: (item.sale_price ?? '') === '' ? '' : Number(item.sale_price),
		})
		setModalCostPrice(bool)
	}

	function toggleModalPriceHistory(bool = false, item = {}) {
		setSelectedItem(item)
		setModalPriceHistory(bool)
	}

	function handlePaginate(page) {
		paginate(page)
		searchWithFilter({ filterData: { ...filterData, page } })
	}

	function handlePageSizeChange(size) {
		if (viewMode === 'tree') return

		const nextFilterData = { ...filterData, size, page: null }
		patchFilterData({ size, page: null })
		searchWithFilter({ filterData: nextFilterData })
	}

	function switchViewMode(mode) {
		setViewMode(mode)
		if (mode === 'table') {
			setExpandedMenuIds(new Set())
		}
		searchWithFilter({
			filterData: { ...filterData, page: null },
			viewMode: mode,
		})
	}

	function toggleMenuCollapse(collapseKey) {
		setExpandedMenuIds((prev) => {
			const next = new Set(prev)
			if (next.has(collapseKey)) next.delete(collapseKey)
			else next.add(collapseKey)
			return next
		})
	}

	function getProductRowIndex(productId) {
		return data.findIndex((item) => Number(item.id) === Number(productId))
	}

	function renderProductRow(item, index, options = {}) {
		const { nameIndent = 0, rowKey = index } = options

		return (
			<tr key={rowKey}>
				<td className="text-center">
					<CustomTooltip text={item.id}>
						{item.row_number}
					</CustomTooltip>
				</td>
				<td>
					<div style={{ paddingLeft: `${nameIndent}px` }}>
						<Link to={`/products/update/${filterData.pos_id}/${item.id}`} target="_blank" rel="noopener noreferrer">
							{item.name}
						</Link>
					</div>
				</td>
				<td className="td-ellipsis">
					{findKeyFromArrayByValue(productTypes, 'id', item.in_product, 'name')
						|| item.product_type_name
						|| item.product_type_value
						|| ''}
				</td>
				<td className="text-end">{formatMoney(item.cost_price)}</td>
				{/* Цена точки из документа цен: правка в списке меняет карточку бренда,
					а такую точку карточка не трогает — поэтому здесь только показываем */}
				{item.is_sale_price_overridden ?
					<td className="text-end">
						<CustomTooltip text={t('sale_price_overridden_hint')}>
							<span>
								<i className="uil uil-lock text-muted me-1"></i>
								{formatMoney(item.sale_price)}
							</span>
						</CustomTooltip>
					</td>
					:
					<td className="td-input td-input-editable">
						<div className="td-editable">
							<i className="uil uil-pen td-editable-icon"></i>
							<input type="number" step="0.01" min="0"
								className="form-control text-end"
								autoComplete="off"
								name="sale_price"
								title={t('press_enter_to_save')}
								value={item.sale_price}
								onChange={(e) => handleInputChange(e, index)}
								onKeyUp={(event) => {
									if (event.key === 'Enter') {
										updateSalePrice(index)
									}
								}}
							/>
						</div>
					</td>
				}
				<td className="text-end">{formatMoney(item.profit)}</td>
				<td className="text-end">{formatMoney(item.cost_price_percent, 2)}</td>
				<td className="text-end">{formatMoney(item.markup_percent, 2)}</td>
				<td className="text-end">{formatMoney(item.profit_percent, 2)}</td>
				<td className="text-center">
					{Number(item.status) === 1
						? <span className="bg-primary bg-border"></span>
						: <span className="bg-danger bg-border"></span>
					}
				</td>
				<td className="text-center">
					<CustomTooltip text={`${t('cost_price')} > ${t('sale_price')}`}>
						{(Number(item.cost_price) > Number(item.sale_price)) &&
							<span className="bg-danger bg-border"></span>
						}
					</CustomTooltip>
					<CustomTooltip text={`${t('cost_price')} <= ${t('sale_price')}`}>
						{(Number(item.cost_price) <= Number(item.sale_price)) &&
							<span className="bg-primary bg-border"></span>
						}
					</CustomTooltip>
				</td>
				<td className="text-center">
					{!item.image &&
						<span className="bg-danger bg-border"></span>
					}
					{item.image &&
						<span className="bg-primary bg-border"></span>
					}
				</td>
				<td className="text-center">
					{!item.has_ingredients &&
						<span className="bg-danger bg-border"></span>
					}
					{item.has_ingredients &&
						<span className="bg-primary bg-border"></span>
					}
				</td>
				<td className="text-center">
					{!item.fiscal_code &&
						<span className="bg-danger bg-border"></span>
					}
					{item.fiscal_code &&
						<span className="bg-primary bg-border"></span>
					}
				</td>
				<td className="text-center">
					{!item.cooking_time &&
						<span className="bg-danger bg-border"></span>
					}
					{item.cooking_time &&
						<span className="bg-primary bg-border" title={item.cooking_time}></span>
					}
				</td>
				<td className="text-center">
					{!item.has_preparation_place &&
						<span className="bg-danger bg-border"></span>
					}
					{item.has_preparation_place &&
						<span className="bg-primary bg-border"></span>
					}
				</td>
				<td>
					<TableDropdown id={`dropdown-${item.id}`}>
						<Link to={`/products/update/${filterData.pos_id}/${item.id}`} className="table-dropdown-item">
							<i className="uil-edit-alt"></i>
							{t('edit')}
						</Link>
						<div className="table-dropdown-item"
							style={{ 'lineHeight': '12px' }}
							onClick={() => toggleModalCostPrice(true, item)}>
							<i className="uil-edit-alt"></i>
							{t('set_cost_price_manually')}
						</div>
						<div className="table-dropdown-item"
							onClick={() => toggleModalPriceHistory(true, item)}>
							<i className="uil-history"></i>
							{t('price_history')}
						</div>
						{item.status === 1 &&
							<div className="table-dropdown-item text-danger"
								onClick={() => {
									setDeleteModalParams({ id: item.id, index: index });
									setModalDelete(true)
								}}>
								<i className="uil-times"></i>
								{t('delete')}
							</div>
						}
						{item.status === -1 &&
							<div className="table-dropdown-item"
								onClick={() => restoreItem(item)}>
								<i className="uil-history-alt"></i>
								{t('restore')}
							</div>
						}
					</TableDropdown>
				</td>
			</tr>
		)
	}

	const PRODUCT_TABLE_EMPTY_MENU_CELLS = 15

	function renderMenuTreeRow(row) {
		const indent = row.depth * 18
		const canToggle = row.hasChildren

		return (
			<tr key={row.key} className="products-tree-menu-row">
				<td></td>
				<td
					className={canToggle ? 'products-tree-menu-name-cell' : ''}
					onClick={canToggle ? () => toggleMenuCollapse(row.collapseKey) : undefined}
					onKeyDown={canToggle ? (event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault()
							toggleMenuCollapse(row.collapseKey)
						}
					} : undefined}
					role={canToggle ? 'button' : undefined}
					tabIndex={canToggle ? 0 : undefined}>
					<div className="d-flex align-items-center" style={{ paddingLeft: `${indent}px` }}>
						{canToggle &&
							<span className="products-tree-toggle me-1 text-dark">
								<i className={`uil uil-angle-${row.isCollapsed ? 'right' : 'down'}`}></i>
							</span>
						}
						{!canToggle &&
							<span className="products-tree-toggle-spacer me-1"></span>
						}
						<i className={`uil ${row.isVirtual ? 'uil-layer-group' : 'uil-folder'} me-1 text-warning`}></i>
						<strong>{row.menu.name}</strong>
						<span className="products-tree-menu-count ms-2">
							{menuProductCounts.get(row.collapseKey) || 0}
						</span>
					</div>
				</td>
				{Array.from({ length: PRODUCT_TABLE_EMPTY_MENU_CELLS }, (_, index) => (
					<td key={`${row.key}-empty-${index}`}></td>
				))}
			</tr>
		)
	}

	async function deleteItem() {
		const response = await DELETE('/products', { 'id': deleteModalParams.id }, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalDelete(false)
		}
	}

	async function restoreItem(item) {
		var sendData = { ...item }
		sendData.id = item.id
		sendData.status = 1
		var response = await PUT('/products-status', sendData, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalDelete(false)
		}
	}

	async function importFromExel(e) {
		let formData = new FormData();
		formData.append("file", e.target.files[0]);

		var response = await FILE('/products-excel-import', formData, {}, { loader: true });

		if (httpOk(response)) {
			notify(t('success'), 'success')
			searchWithFilter()
		}

		document.getElementById('actual-btn').value = '';
	}

	async function exportToScale() {
		var response = await FILE_D('/products-export-scale')
		if (httpOk(response)) {
			// Создаем объект Blob из полученного файла
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

			// Создаем ссылку для скачивания файла
			const fileUrl = URL.createObjectURL(file);

			// Создаем ссылку для скачивания файла в браузере
			const link = document.createElement('a');
			link.href = fileUrl;
			link.setAttribute('download', `scale.txt`);
			document.body.appendChild(link);
			link.click();

			// Удаляем ссылку после скачивания файла
			URL.revokeObjectURL(fileUrl);
		}
	}

	async function updateTaxCategory() {
		const response = await PUT('/products-tax-categories', selectedItem, { loader: true })
		if (httpOk(response)) toggleModalTaxCategory()
	}

	async function updateCategory() {
		const response = await PUT('/products-categories', selectedItem, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			toggleModalCategory()
		}
	}

	async function updateProductOptions() {
		const payload = { ...selectedItem }
		const response = await PUT('/products-options', payload, { loader: true })
		if (httpOk(response)) toggleModalOption()
	}

	async function updateFiscalCodes() {
		const response = await PUT('/products-fiscalization', selectedItem, { loader: true })
		if (httpOk(response)) toggleModalFiscal()
	}

	async function updateCookingTime() {
		const response = await PUT('/products-cooking-time', selectedItem, { loader: true })
		if (httpOk(response)) {
			notify(t('success'), 'success')
			searchWithFilter()
			toggleModalCookingTime()
		}
	}

	async function updatePreparationPlace() {
		const response = await PUT('/products-preparation-places', selectedItem, { loader: true })
		if (httpOk(response)) {
			notify(t('success'), 'success')
			searchWithFilter()
			toggleModalPreparationPlace()
		}
	}

	async function updateWms() {
		const response = await PUT('/products-wms', selectedItem, { loader: true })
		if (httpOk(response)) {
			notify(t('success'), 'success')
			searchWithFilter()
			toggleModalWms()
		}
	}

	async function updateWriteOffType() {
		const response = await PUT('/products-write-off-type', selectedItem, { loader: true })
		if (httpOk(response)) {
			notify(t('success'), 'success')
			searchWithFilter()
			toggleModalWriteOffType()
		}
	}

	async function handleSelectPos(option) {
		if (!option) {
			setSelectedItem({ ...selectedItem, 'pos_id': null, 'preparation_place_id': null, 'wms_id': null })
			return
		}

		var response = await GET(`/helper/preparation-places?pos_id=${option.id}`, {}, { loader: true })
		if (httpOk(response)) setPreparationPlaces(response.data)

		const wmsResponse = await GET(`/helper/wms?pos_id=${option.id}`, {}, { loader: true })
		if (httpOk(wmsResponse)) setWms(wmsResponse.data)

		setSelectedItem({ ...selectedItem, 'pos_id': option.id, 'preparation_place_id': null, 'wms_id': null })
	}

	function handleSelectFiscalCode(option) {
		if (!option) {
			setSelectedItem({ ...selectedItem, 'fiscal_code': null, 'fiscal_package_code': null })
			return
		}

		setSelectedItem({ ...selectedItem, 'fiscal_code': option.id, 'fiscal_package_code': null })
		setFiscalPackageCodes(option.units)
	}

	async function toggleModalFiscal(bool = false) {
		if (bool && !fiscalCodes.length) {
			var response = await GET(`/helper/fiscal-codes`, {}, { loader: true })
			if (httpOk(response)) setFiscalCodes(response.data)
		}

		if (!bool) setSelectedItem({})

		setModalFiscal(bool)
	}

	function toggleModalCookingTime(bool = false) {
		if (!bool) setSelectedItem({})
		else setSelectedItem({ menu: [], cooking_time: '' })

		setModalCookingTime(bool)
	}

	async function toggleModalTaxCategory(bool = false) {
		if (!bool) setSelectedItem({})

		if (bool && !taxCategories.length) {
			var response = await GET(`/helper/tax-categories`, {}, { loader: true })
			if (httpOk(response)) setTaxCategories(response.data)
		}

		setModalTaxCategory(bool)
	}

	async function toggleModalCategory(bool = false) {
		if (!bool) setSelectedItem({})
		if (bool) setSelectedItem({ menu: [], category_id: null })

		setModalCategory(bool)
	}

	async function toggleModalPreparationPlace(bool = false) {
		if (!bool) setSelectedItem({})
		else setSelectedItem({ menu: [], pos_id: filterData.pos_id || null, preparation_place_id: null })

		setModalPreparationPlace(bool)
	}

	async function toggleModalWms(bool = false) {
		if (!bool) {
			setSelectedItem({})
		} else {
			const posId = filterData.pos_id || null
			if (posId) await getWms(posId)
			setSelectedItem({ menu: [], pos_id: posId, wms_id: null })
		}

		setModalWms(bool)
	}

	async function toggleModalWriteOffType(bool = false) {
		if (!bool) setSelectedItem({})
		else setSelectedItem({ menu: [], write_off_type_id: 1 })

		setModalWriteOffType(bool)
	}

	async function toggleModalOption(bool = false) {
		if (bool) {
			setSelectedItem({
				'status': true,
				'is_divisible': true,
				'is_custom_price': false,
				'is_external_menu_enabled': true,
				'is_print_ingredients': false,
				'is_service_charge_enabled': true,
				'is_stock_controlled': false,
				'is_standalone': false,
				'is_marking': false,
				'menu': [],
			})
		}

		setModalOption(bool)
	}

	async function toggleModalMultipleDelete(bool = false) {
		if (bool) {
			setSelectedItem({ ...selectedItem, delete_all: false })
		}
		setModalMultipleDelete(bool)
	}

	async function getPoses() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	async function getMenu() {
		var response = await GET('/helper/menu')
		if (httpOk(response)) {
			setMenu(response.data)
			return response.data || []
		}
		return []
	}

	async function getCategories() {
		const response = await GET('/helper/categories')
		if (httpOk(response)) setCategories(response.data)
	}

	async function getPreparationPlaces(pos_id = null) {
		var filterPosId = pos_id ?? filterData.pos_id
		var response = await GET(`/helper/preparation-places?pos_id=${filterPosId}`)
		if (httpOk(response)) setPreparationPlaces(response.data)
	}

	async function getWms(pos_id = null) {
		const filterPosId = pos_id ?? filterData.pos_id
		const response = await GET(`/helper/wms?pos_id=${filterPosId}`)
		if (httpOk(response)) setWms(response.data)
	}

	function handlePosFilterChange(option) {
		const nextPosId = option?.id ?? null
		patchFilterData({
			pos_id: nextPosId,
			wms_id: null,
		})
		if (nextPosId) {
			getPreparationPlaces(nextPosId)
			getWms(nextPosId)
		} else {
			setPreparationPlaces([])
			setWms([])
		}
	}

	async function getProductTypes() {
		const response = await GET(`/helper/product-types`)
		if (httpOk(response)) setProductTypes(response.data)
	}

	const flattenTree = (items, parentId = null, depth = 0, ancestors = new Set()) => {
		let result = [];
		const currentLevel = (items || []).filter(item => item.parent_id === parentId);

		currentLevel.forEach(item => {
			const menuId = Number(item.id)
			if (ancestors.has(menuId)) return

			const nextAncestors = new Set(ancestors)
			nextAncestors.add(menuId)
			const children = flattenTree(items, item.id, depth + 1, nextAncestors);
			const childrenNames = children.map(c => c.name).join(' ');

			const prefix = '\u00A0'.repeat(depth * 5);
			result.push({
				...item,
				displayLabel: prefix + (depth > 0 ? '↳ ' : '') + item.name,
				depth: depth,
				searchString: `${item.name} ${childrenNames}`.toLowerCase()
			});

			result = result.concat(children);
		});

		return result;
	};

	// Просто создаем переменную. Если menu обновится (придет с бэка), 
	// компонент перерисуется, и переменная создастся заново.
	const menus = flattenTree(menu || [], null);
	const selectedMenus = filterData.menu_ids || []
	const allMenusSelected = menus.length > 0 && selectedMenus.length === menus.length
	const treeRows = useMemo(
		() => buildMenuProductTreeRows(menu, data, expandedMenuIds, t('products_without_menu')),
		[menu, data, expandedMenuIds, t]
	)
	const menuProductCounts = useMemo(
		() => buildMenuProductCounts(menu, data, { includeWithoutMenu: true }),
		[menu, data]
	)

	function renderMenuMultiValue(props) {
		if (allMenusSelected) {
			if (props.index > 0) return null
			return (
				<components.MultiValue {...props}>
					<span className="px-1">{t('all_menus')}</span>
				</components.MultiValue>
			)
		}

		const maxVisible = 2
		if (props.index < maxVisible) {
			return <components.MultiValue {...props} />
		}
		if (props.index === maxVisible) {
			return (
				<components.MultiValue {...props}>
					<span className="px-1">+{selectedMenus.length - maxVisible}</span>
				</components.MultiValue>
			)
		}
		return null
	}

	function renderMenuMultiValueRemove(props) {
		if (allMenusSelected) {
			return (
				<components.MultiValueRemove
					{...props}
					innerProps={{
						...props.innerProps,
						onClick: (e) => {
							e.preventDefault()
							e.stopPropagation()
							patchFilterData({ menu_ids: [] })
						},
					}}
				/>
			)
		}
		return <components.MultiValueRemove {...props} />
	}

	const appliedOrderParsed = parseProductTableOrderBy(appliedFilterData.order_by)

	// Товар сохранили в другой вкладке (карточка открывается в новой) — тихо перечитываем список с применёнными фильтрами
	const refreshOnProductsChangedRef = useRef()
	refreshOnProductsChangedRef.current = () => searchWithFilter({ filterData: appliedFilterData, silent: true })

	useEffect(() => {
		return subscribeProductsChanged(() => refreshOnProductsChangedRef.current())
	}, [])

	useEffect(() => {
		async function initPage() {
			getPoses()
			getCategories()
			getProductTypes()
			getPreparationPlaces()
			getWms()

			const menuData = await getMenu()
			const allMenus = flattenTree(menuData || [], null)
			const searchParams = new URLSearchParams(location.search)
			const typeRaw = searchParams.get('product_type_id')
			const productTypeId = typeRaw !== null && typeRaw !== '' && !Number.isNaN(Number(typeRaw))
				? Number(typeRaw)
				: null
			// Заготовки (2) и модификаторы (3) не привязаны к меню — не подставляем фильтр
			// по меню, иначе они (menu_id IS NULL) отсекаются и список пустой.
			const menuLessTypes = [2, 3]
			const hasMenu = productTypeId === null || !menuLessTypes.includes(productTypeId)
			const nextFilterData = {
				pos_id: reduxAccount.pos_id,
				wms_id: null,
				status: 1,
				size: 20,
				search: '',
				category_id: null,
				menu_ids: hasMenu ? allMenus : [],
				product_type_id: productTypeId,
			}
			setFilterData(nextFilterData)
			await searchWithFilter({ filterData: nextFilterData })
		}

		initPage()
	}, [location.search]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('products')} [{pagination?.response?.total}]
				</h4>
				<div className="d-flex gap-2">
					<div className="btn btn-primary"
						title="AI"
						onClick={() => setModalAi(true)}>
						AI
					</div>
					<Link className="btn-rounded btn btn-primary"
						to={`/products/create/${filterData.pos_id}${filterData.product_type_id ? `?product_type_id=${filterData.product_type_id}` : ''}`}>
						{t('create')}
					</Link>
					<DropdownButton title={t('action')}>
						<Dropdown.Item as="li" eventKey="1">
							<input id="actual-btn" hidden type="file" onChange={importFromExel} accept=".xlsx, application/vnd.ms-excel" />
							<label htmlFor="actual-btn" className="upload-from-excel">{t('import')}</label>
						</Dropdown.Item>
						<Dropdown.Item as="li" eventKey="2">
							<a href={`${process.env.REACT_APP_DOWNLOAD_DOMAIN}/files/products_template.xlsx`} className="upload-from-excel">
								{t('download_template')}
							</a>
						</Dropdown.Item>
						<Dropdown.Item eventKey="3" onClick={exportToScale}>
							{t('export_scale')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="4" onClick={() => toggleModalFiscal(true)}>
							{t('fiscalization')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="10" onClick={() => toggleModalCookingTime(true)}>
							{t('cooking_time')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="5" onClick={() => toggleModalPreparationPlace(true)}>
							{t('preparation_places')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="11" onClick={() => toggleModalWms(true)}>
							{t('wms')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="12" onClick={() => toggleModalWriteOffType(true)}>
							{t('write_off_method')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="6" onClick={() => toggleModalTaxCategory(true)}>
							{t('tax_categories')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="7" onClick={() => toggleModalCategory(true)}>
							{t('categories')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="8" onClick={() => toggleModalOption(true)}>
							{t('options')}
						</Dropdown.Item>
						<Dropdown.Item eventKey="9" onClick={() => toggleModalMultipleDelete(true)}>
							{t('delete')}
						</Dropdown.Item>
					</DropdownButton>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={(search) => applySearch(search, (nextFilterData) => {
										searchWithFilter({ filterData: nextFilterData })
									})}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={handlePosFilterChange}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>

											<div className="form-group col-md-6">
												<label>{t('menu')}</label>
												<Select
													isMulti
													isClearable
													placeholder=""
													options={menus}
													value={selectedMenus}
													onChange={(options) => patchFilterData({ 'menu_ids': options ? options : [] })}
													getOptionLabel={(option) => option.displayLabel}
													getOptionValue={(option) => option.id}
													components={{
														MultiValue: renderMenuMultiValue,
														MultiValueRemove: renderMenuMultiValueRemove,
													}}
													filterOption={(option, inputValue) => {
														const searchVal = inputValue.toLowerCase();
														return option.data.searchString.includes(searchVal);
													}}
													styles={{
														control: (base) => ({
															...base,
															minHeight: 38,
														}),
														valueContainer: (base) => ({
															...base,
															flexWrap: 'nowrap',
															overflow: 'hidden',
														}),
														multiValue: (base) => ({
															...base,
															maxWidth: 140,
														}),
														option: (base) => ({
															...base,
															whiteSpace: 'pre',
														}),
													}}
												/>
											</div>

											{/* <div className="form-group col-md-6">
												<label>{t('menu')}</label>
												<Select
													isClearable
													options={menu}
													value={menu.find(option => option.id === filterData.menu_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'menu_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div> */}
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('type')}</label>
												<Select
													isClearable
													options={productTypes}
													value={productTypes.find(option => option.id === filterData.product_type_id) ?? null}
													onChange={(option) => patchFilterData({
														'product_type_id': option ? option.id : null,
													})}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('category')}</label>
												<Select
													isClearable
													options={categories}
													value={categories.find(option => option.id === filterData.category_id) || ''}
													onChange={(option) => patchFilterData({ category_id: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('preparation_places')}</label>
												<Select
													isClearable
													options={preparationPlaces}
													value={preparationPlaces.find(option => option.id === filterData.preparation_place_id) || ''}
													onChange={(option) => patchFilterData({ 'preparation_place_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('wms')}</label>
												<Select
													isClearable
													options={wms}
													value={wms.find(option => option.id === filterData.wms_id) ?? null}
													onChange={(option) => patchFilterData({ wms_id: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('products_without')}</label>
												<Select
													isClearable
													options={columnFilers}
													value={columnFilers.find(option => option.id === filterData.product_without) || ''}
													onChange={(option) => patchFilterData({ 'product_without': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={statuses}
													value={statuses.find(option => option.id === filterData.status) || ''}
													onChange={(option) => patchFilterData({ 'status': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('margin')}</label>
												<div className="d-flex gap-2">
													<input type="text" className="form-control w-100"
														name="from_profit_percent"
														autoComplete="off"
														placeholder={t('from')}
														onChange={(e) => patchFilterData({ 'from_profit_percent': e.target.value })} />
													<input type="text" className="form-control w-100"
														name="to_profit_percent"
														autoComplete="off"
														placeholder={t('to')}
														onChange={(e) => patchFilterData({ 'to_profit_percent': e.target.value })} />
												</div>
											</div>
										</div>
										<div className="d-flex justify-content-end gap-2">
											<button className="btn btn-secondary"
												onClick={() => setDropdownIsOpen(false)}>
												{t('cancel')}
											</button>
											<button className="btn btn-primary"
												onClick={() => applyFilters((nextFilterData) => {
													searchWithFilter({ filterData: nextFilterData })
												})}>
												{t('apply')}
											</button>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div className="d-flex gap-1">
								<button
									type="button"
									className={`btn btn-outline-primary btn-wide-2 ${viewMode === 'table' ? 'active' : ''}`}
									onClick={() => switchViewMode('table')}
									title={t('products_view_table')}>
									<i className="uil uil-list-ul"></i>
								</button>
								<button
									type="button"
									className={`btn btn-outline-primary btn-wide-2 ${viewMode === 'tree' ? 'active' : ''}`}
									onClick={() => switchViewMode('tree')}
									title={t('products_view_tree')}>
									<i className="uil uil-folder"></i>
								</button>
							</div>
							<div className="d-flex gap-2">
								<button className="btn btn-outline-primary btn-wide"
									onClick={() => downloadExcelRecipeFile()}>
									{t('recipes')}
								</button>
								<button className="btn btn-outline-primary btn-wide-2"
									onClick={() => downloadExcel()}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{appliedFilterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', appliedFilterData.pos_id, 'name')}
									</span>
								}
								{appliedFilterData.wms_id &&
									<span className="me-2">
										<b>{t('wms')}: </b>
										{findKeyFromArrayByValue(wms, 'id', appliedFilterData.wms_id, 'name')}
									</span>
								}
								{appliedFilterData.category_id &&
									<span className="me-2">
										<b>{t('category')}: </b>
										{findKeyFromArrayByValue(categories, 'id', appliedFilterData.category_id, 'name')}
									</span>
								}
								{appliedFilterData.menu_id &&
									<span className="me-2">
										<b>{t('menu')}: </b>
										{findKeyFromArrayByValue(menu, 'id', appliedFilterData.menu_id, 'name')}
									</span>
								}
								{!!appliedFilterData.menu_ids?.length &&
									<span className="me-2">
										<b>{t('menu')}: </b>
										{menus.length > 0 && appliedFilterData.menu_ids.length === menus.length
											? t('all_menus')
											: appliedFilterData.menu_ids.length <= 3
												? appliedFilterData.menu_ids.map((item) => item.name).join(', ')
												: `${appliedFilterData.menu_ids.length}`}
									</span>
								}
								{appliedFilterData.preparation_place_id &&
									<span className="me-2">
										<b>{t('preparation_places')}: </b>
										{findKeyFromArrayByValue(preparationPlaces, 'id', appliedFilterData.preparation_place_id, 'name')}
									</span>
								}
								{appliedFilterData.product_type_id != null && appliedFilterData.product_type_id !== '' &&
									<span className="me-2">
										<b>{t('type')}: </b>
										{findKeyFromArrayByValue(
											productTypes,
											'id',
											appliedFilterData.product_type_id,
											'name'
										)}
									</span>
								}
								{appliedFilterData.status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{findKeyFromArrayByValue(statuses, 'id', appliedFilterData.status, 'name')}
									</span>
								}
								{appliedFilterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{appliedFilterData.search}</span>
									</span>
								}
								{appliedFilterData.order_by && appliedOrderParsed.field && (
									<span className="me-2 d-inline-flex align-items-center flex-wrap gap-1">
										<b>{t("sort_by")}: </b>
										<span className="d-inline-flex align-items-center gap-1">
											{productSortFieldLabel(appliedOrderParsed.field)}
											{renderSortDirectionIcon(appliedOrderParsed.sort, {
												ariaLabel:
													appliedOrderParsed.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
												title:
													appliedOrderParsed.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
											})}
										</span>
									</span>
								)}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive table-products-sticky"
						ref={tableDragScroll.ref}
						onMouseDown={tableDragScroll.onMouseDown}
						onMouseMove={tableDragScroll.onMouseMove}
						onMouseUp={tableDragScroll.onMouseUp}
						onMouseLeave={tableDragScroll.onMouseLeave}
						onClickCapture={tableDragScroll.onClickCapture}>
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '40px' }} className="text-center">#</th>
									<th
										style={{ width: "100%", minWidth: "260px" }}
										className="th-sortable user-select-none"
										onClick={() => handleProductTableSort("name")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-between gap-2 pe-1">
											<span>{t("name2")}</span>
											{renderSortIndicator("name", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th style={{ minWidth: "100px" }} className="td-ellipsis">{t("type")}</th>
									<th style={{ minWidth: "120px" }} className="text-end td-ellipsis" title={t("cost_price_recipe")}>{t("cost_price_recipe")}</th>
									<th
										style={{ minWidth: "130px" }}
										className="text-end td-ellipsis th-sortable user-select-none"
										onClick={() => handleProductTableSort("sale_price")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2">
											<span>
												<i className="uil uil-pen me-1"></i>
												{t("sale_price")}
											</span>
											{renderSortIndicator("sale_price", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th
										style={{ minWidth: "110px" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleProductTableSort("profit")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2">
											<span>{t("income")}</span>
											{renderSortIndicator("profit", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th
										style={{ minWidth: "110px" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleProductTableSort("cost_price_percent")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2">
											<CustomTooltip text={`${t("cost_price")} / ${t("sale_price")} * 100`}>
												<span>{t("cost_price")}%</span>
											</CustomTooltip>
											{renderSortIndicator("cost_price_percent", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th
										style={{ minWidth: "110px" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleProductTableSort("markup_percent")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2">
											<span>{t("markup")}%</span>
											{renderSortIndicator("markup_percent", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th
										style={{ minWidth: "110px" }}
										className="text-end th-sortable user-select-none"
										onClick={() => handleProductTableSort("profit_percent")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2">
											<CustomTooltip text={`(${t("sale_price")} - ${t("cost_price")}) / ${t("sale_price")} * 100`}>
												<span>{t("margin")}%</span>
											</CustomTooltip>
											{renderSortIndicator("profit_percent", productSortStateForIndicator(filterData))}
										</div>
									</th>
									<th style={{ minWidth: "70px" }} className="text-center td-ellipsis"
										title={t('on_sale')}>
										{t('on_sale')}
									</th>
									<th style={{ 'minWidth': '55px' }} className="text-center td-ellipsis"
										title={t('price')}>
										{t('price')}
									</th>
									<th style={{ 'minWidth': '55px' }} className="text-center td-ellipsis"
										title={t('image2')}>
										{t('image2')}
									</th>
									<th style={{ 'minWidth': '55px' }} className="text-center td-ellipsis"
										title={t('ingredients')}>
										{t('ingredients')}
									</th>
									<th style={{ 'minWidth': '55px' }} className="text-center td-ellipsis"
										title={t('fiscalization')}>
										{t('fiscalization')}
									</th>
									<th style={{ 'minWidth': '70px' }} className="text-center td-ellipsis"
										title={t('cooking_time')}>
										{t('cooking_time')}
									</th>
									<th style={{ 'minWidth': '55px' }} className="text-center td-ellipsis"
										title={t('preparation_places')}>
										{t('preparation_places')}
									</th>
									<th style={{ 'width': '33px', 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{viewMode === 'table' && data?.map((item, index) => renderProductRow(item, index))}
								{viewMode === 'tree' && treeRows.map((row) => {
									if (row.rowType === 'menu') {
										return renderMenuTreeRow(row)
									}

									const productIndex = getProductRowIndex(row.product.id)
									if (productIndex < 0) return null

									return renderProductRow(row.product, productIndex, {
										nameIndent: row.depth * 18,
										rowKey: row.key,
									})
								})}
							</tbody>
						</table>
					</div>

					{viewMode === 'table' && pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL CREATE */}
			<Modal show={modalCostPrice} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalCostPrice()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('edit')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={(e) => updateCostPrice(e)} autoComplete="off">
						<div className="form-group w-100">
							<label>{t('cost_price')}</label>
							<input type="number" className="form-control" name="cost_price"
								value={selectedItem.cost_price ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'cost_price': e.target.value })} />
						</div>
						<div className="form-group w-100">
							<label>{t('sale_price')}</label>
							<input type="number" className="form-control" name="sale_price"
								value={selectedItem.sale_price ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'sale_price': e.target.value })} />
						</div>

						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalCostPrice(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL CREATE */}

			<PriceHistory
				show={modalPriceHistory}
				product={selectedItem}
				posId={filterData.pos_id}
				onHide={() => toggleModalPriceHistory(false)} />

			{/* MODAL FISCAL */}
			<Modal show={modalFiscal} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalFiscal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('fiscalization')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>ИКПУ</label>
							<Select
								isClearable
								options={fiscalCodes}
								value={fiscalCodes.find(option => option.id === selectedItem.fiscal_code)}
								onChange={(option) => handleSelectFiscalCode(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("unit")}</label>
							<Select
								isClearable
								options={fiscalPackageCodes}
								value={fiscalPackageCodes.find(option => option.id === selectedItem.fiscal_package_code)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'fiscal_package_code': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalFiscal()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updateFiscalCodes()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL FISCAL */}

			{/* MODAL COOKING TIME */}
			<Modal show={modalCookingTime} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalCookingTime(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('cooking_time')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options || [] })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<label>{t('cooking_time')}</label>
						<MaskedInput
							mask={[/[0-9]/, /\d/, ':', /\d/, /\d/, ':', /\d/, /\d/]}
							className="form-control"
							placeholder="00:00:00"
							guide={true}
							value={selectedItem.cooking_time ?? ''}
							onChange={(e) => setSelectedItem({ ...selectedItem, 'cooking_time': e.target.value })}
						/>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalCookingTime()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updateCookingTime()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL COOKING TIME */}

			{/* MODAL PREPARATION PLACE */}
			<Modal show={modalPreparationPlace} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalPreparationPlace(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('preparation_places')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("poses")}</label>
							<Select
								options={poses}
								value={poses.find(option => option.id === selectedItem.pos_id)}
								onChange={(option) => handleSelectPos(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("preparation_places")}</label>
							<Select
								isClearable
								options={preparationPlaces}
								value={preparationPlaces.find(option => option.id === selectedItem.preparation_place_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'preparation_place_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalPreparationPlace()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updatePreparationPlace()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL PREPARATION PLACE */}

			{/* MODAL WMS */}
			<Modal show={modalWms} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalWms(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('wms')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("poses")}</label>
							<Select
								options={poses}
								value={poses.find(option => option.id === selectedItem.pos_id)}
								onChange={(option) => handleSelectPos(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("wms")}</label>
							<Select
								options={wms}
								value={wms.find(option => option.id === selectedItem.wms_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'wms_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalWms()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							disabled={!selectedItem.pos_id || !selectedItem.wms_id || !(selectedItem.menu?.length)}
							onClick={() => updateWms()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL WMS */}

			{/* MODAL WRITE OFF TYPE */}
			<Modal show={modalWriteOffType} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalWriteOffType(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('write_off_method')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t("write_off_method")}</label>
							<Select
								options={writeOffTypes}
								value={writeOffTypes.find(option => option.id === selectedItem.write_off_type_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'write_off_type_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalWriteOffType()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							disabled={!selectedItem.write_off_type_id || !(selectedItem.menu?.length)}
							onClick={() => updateWriteOffType()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL WRITE OFF TYPE */}

			{/* MODAL TAX CATEGORY */}
			<Modal show={modalTaxCategory} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalTaxCategory(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('tax_categories')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<div className="form-group">
							<label>{t("poses")}</label>
							{` - `}
							<span className="clickable"
								onClick={() => setSelectedItem({ ...selectedItem, 'poses': poses })}>
								{t('add_all')}
							</span>
							<Select
								isMulti
								options={poses}
								value={selectedItem.poses}
								onChange={(options) => setSelectedItem({ ...selectedItem, 'poses': options })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t('tax_categories')}</label>
							<Select
								isClearable
								options={taxCategories}
								value={taxCategories.find(option => option.id === selectedItem.tax_category_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'tax_category_id': option.id })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalTaxCategory()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updateTaxCategory()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL TAX CATEGORY */}

			{/* MODAL CATEGORY */}
			<Modal show={modalCategory} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalCategory(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('categories')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<div className="form-group w-100">
						<div className="form-group">
							<label>{t('categories')}</label>
							<Select
								isClearable
								options={categories}
								value={categories.find(option => option.id === selectedItem.category_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'category_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</div>
					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalCategory()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updateCategory()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL CATEGORY */}

			{/* MODAL OPTION */}
			<Modal show={modalOption} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalOption()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('options')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menu': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menu}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menu': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('on_sale')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="status"
								checked={selectedItem?.status}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'status': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('devisible_product')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_divisible"
								checked={selectedItem?.is_divisible}
								onChange={(e) => setSelectedItem({
									...selectedItem,
									'is_divisible': e.target.checked,
								})}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('custom_price')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_custom_price"
								checked={selectedItem?.is_custom_price}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_custom_price': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('external_service')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_external_menu_enabled"
								checked={selectedItem?.is_external_menu_enabled}
								onChange={(e) => setSelectedItem({
									...selectedItem,
									'is_external_menu_enabled': e.target.checked,
								})}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('print_ingredients_kitchen')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_print_ingredients"
								checked={selectedItem?.is_print_ingredients}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_print_ingredients': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('is_service_charge_enabled')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_service_charge_enabled"
								checked={selectedItem?.is_service_charge_enabled}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_service_charge_enabled': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center text-danger">{t('is_standalone')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_standalone"
								checked={selectedItem?.is_standalone}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_standalone': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center text-danger">{t('inventory_basaed_stop_list')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_stock_controlled"
								checked={selectedItem?.is_stock_controlled}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_stock_controlled': e.target.checked })}
							/>
						</div>
					</div>
					<div className="d-flex justify-content-between">
						<p className="fw-600 vertical-center">{t('marking')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="is_marking"
								checked={!!selectedItem?.is_marking}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_marking': e.target.checked })}
							/>
						</div>
					</div>

					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalOption()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => updateProductOptions()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL OPTION */}

			{/* MODAL MULTIPLE DELETE */}
			<Modal show={modalMultipleDelete} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModalMultipleDelete()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('delete')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group">
						<label>{t("types")}</label>
						<Select
							isMulti
							options={productTypes}
							value={selectedItem.product_types}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'product_types': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div className="form-group">
						<label>{t("menu")}</label>
						{` - `}
						<span className="clickable"
							onClick={() => setSelectedItem({ ...selectedItem, 'menus': menu })}>
							{t('add_all')}
						</span>
						<Select
							isMulti
							options={menu}
							value={selectedItem.menus}
							onChange={(options) => setSelectedItem({ ...selectedItem, 'menus': options })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div className="form-check mb-3">
						<input
							className="form-check-input"
							type="checkbox"
							id="delete-all-products"
							checked={Boolean(selectedItem.delete_all)}
							onChange={(event) => setSelectedItem({ ...selectedItem, delete_all: event.target.checked })}
						/>
						<label className="form-check-label" htmlFor="delete-all-products">
							{t('delete_all')}
						</label>
					</div>

					<div className="d-flex gap-2 mt-5 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalMultipleDelete()}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => deleteMultipleProducts()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL MULTIPLE DELETE */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			<ProductsAiModal
				show={modalAi}
				onHide={() => setModalAi(false)}
				filterData={appliedFilterData}
				onApplied={() => searchWithFilter()}
			/>
		</>
	)
}

export default Index